/**
 * CommunicationService - Handles patient communication API operations
 */
export class CommunicationService {
  constructor(apiClient) {
    this.apiClient = apiClient;
    console.log('CommunicationService initialized');
  }

  /**
   * Fetch all communications sent for a document
   * @param {string} documentId
   * @returns {Promise<Array>} Array of PatientComm objects
   */
  async fetchDocumentComms(documentId, options = {}) {
    return this.apiClient.get(`/api/document/${documentId}/comms/fetch`, options);
  }

  /**
   * Generate message content for a document
   * @param {string} documentId
   * @returns {Promise<Object>} DocumentCommResponse with content field
   */
  async generateMessageContent(documentId) {
    return this.apiClient.get(`/api/document/${documentId}/comm/patient/generate`);
  }

  /**
   * Send a message to a patient
   * @param {string} documentId
   * @param {Object} commRequest - PatientCommRequest object
   * @returns {Promise<void>}
   */
  async sendMessage(documentId, commRequest) {
    const requestBody = {
      documentId,
      commRequest
    };
    return this.apiClient.post('/api/document/comm/send', requestBody);
  }

  /**
   * Generate contract violation letter content
   * @param {string} documentId
   * @param {string} contractViolation - LmcContractViolationType enum value
   * @returns {Promise<Object>} DocumentCommResponse with content field
   */
  async generateContractViolationLetter(documentId, contractViolation) {
    return this.apiClient.get(`/api/document/${documentId}/comm/lmc/generate?contract_violation=${contractViolation}`);
  }

  async fetchQualityAlertTemplates() {
    return this.apiClient.get('/api/quality-alert/templates');
  }

  async generateQualityAlert(documentId, request) {
    return this.apiClient.post(`/api/document/${documentId}/quality-alert/generate`, request);
  }

  /**
   * Fetch all communication templates
   * @returns {Promise<Array>} Array of CommTemplate objects
   */
  async fetchCommTemplates() {
    return this.apiClient.get('/api/document/comm/templates/fetch');
  }

  /**
   * Create a new communication template
   * @param {Object} template - CommTemplate object with templateId, title, and content
   * @returns {Promise<void>}
   */
  async createCommTemplate(template) {
    return this.apiClient.post('/api/document/comm/template/create', template);
  }

  /**
   * Update an existing communication template
   * @param {Object} templateUpdate - CommTemplateUpdate object with templateId, title, and content
   * @returns {Promise<void>}
   */
  async updateCommTemplate(templateUpdate) {
    return this.apiClient.post('/api/document/comm/template/update', templateUpdate);
  }

  /**
   * Delete a communication template
   * @param {string} templateId - The ID of the template to delete
   * @returns {Promise<void>}
   */
  async deleteCommTemplate(templateId) {
    return this.apiClient.post(`/api/document/comm/template/${templateId}/delete`);
  }

  /**
   * Fetch all communications sent for a consultation
   * @param {string} consultationId
   * @returns {Promise<Array>} Array of PatientComm objects
   */
  async fetchConsultationComms(consultationId, options = {}) {
    return this.apiClient.get(`/api/consultation/${consultationId}/comms/fetch`, options);
  }

  /**
   * Generate message content for a consultation
   * @param {string} consultationId
   * @returns {Promise<Object>} ConsultationCommResponse with content field
   */
  async generateConsultationMessageContent(consultationId) {
    return this.apiClient.get(`/api/consultation/${consultationId}/comm/patient/generate`);
  }

  /**
   * Send a message to a patient for a consultation
   * @param {string} consultationId
   * @param {Object} commRequest - PatientCommRequest object
   * @returns {Promise<void>}
   */
  async sendConsultationMessage(consultationId, commRequest) {
    const requestBody = {
      consultationId,
      commRequest
    };
    return this.apiClient.post('/api/consultation/comm/send', requestBody);
  }
}

